// apps/landing-page/src/hooks/usePaymentMethods.ts

import { useEffect, useState } from 'react';
import { paymentApi, type PaymentMethodResponse } from '@/lib/api/payments';

interface UsePaymentMethodsResult {
  methods: PaymentMethodResponse[];
  isLoading: boolean;
  error: boolean;
}

/**
 * GET /v1/payment/methods, fetched ONCE per caller. Meant to be called once
 * per page (test/course detail) and the result threaded down as props to
 * however many per-card consumers (e.g. QrisTeaser) that page renders,
 * rather than each card calling this itself.
 *
 * No `amount` filter here (unlike CartDrawer's checkout-time call) — callers
 * that only need one category (e.g. QRIS) filter the returned list
 * themselves so the same fetch can serve every consumer on the page
 * regardless of that consumer's own price.
 *
 * Mirrors apps/dashboard/src/hooks/usePaymentMethods.ts — keep in sync.
 */
export function usePaymentMethods(): UsePaymentMethodsResult {
  const [methods, setMethods] = useState<PaymentMethodResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    paymentApi
      .getPaymentMethods()
      .then((result) => {
        if (cancelled) return;
        setMethods(result);
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setMethods([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { methods, isLoading, error };
}
